import React from 'react';
import * as Sentry from '@sentry/react';
import {useTranslation} from 'react-i18next';
import fetcher from '../utils/fetcher';
import {IMAGE_SERVICE_ORIGIN, IV_PHOTO_SOURCE, OCRDetectedData} from '@guestapp/sdk';
import {useChekinSDK} from '../context/ChekinSDK';
import {OCR_CHECKING_STATUSES} from '../utils/constants';
import {DOCUMENTS_TYPES, getRemappedOfficialDocType} from '../utils/docTypes';
import {ANALYTIC_EVENTS, Analytics} from '../analytics';
import {getIVIntentRemoteSession} from 'hooks/iv/useIVRemoteSync';

const MaxRetriesNumber = 40;
const RetryTimeoutMs = 1000;

const isBarcodeDetection = (documentType = '') => {
  return [DOCUMENTS_TYPES.drivingLicenseUS, DOCUMENTS_TYPES.drivingLicenseCA].includes(
    documentType,
  );
};

type IVCheckProps = {
  image: string;
  countryCode: string;
  source: IV_PHOTO_SOURCE;
  reservationId: string;
  documentType?: string;
  nationality?: string;
};

type CheckOCRStatusProps = {
  image: string;
  processId: string;
  retries?: number;
  documentType?: string;
};

type checkOCRResponse =
  | {
      mrzDetected?: boolean;
      detectionFailed?: boolean;
    }
  | undefined;

type UseDocumentSelfieDetection = {
  onError?: (error: Error) => void;
  onFinish?: (image: string, data?: OCRDetectedData) => void;
  onBefore?: () => void;
  onDetectionFailed?: (
    errorMessage: string,
    data: OCRDetectedData & {image: string},
  ) => void;
  onRetriesFinish?: (data: OCRDetectedData, retries: number) => void;
};

function useOCR({
  onFinish,
  onBefore,
  onError,
  onDetectionFailed,
  onRetriesFinish,
}: UseDocumentSelfieDetection) {
  const {t} = useTranslation();
  const {sendImageForOCR, checkImageOCRStatus} = useChekinSDK();
  const retryTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>();
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout>>();

  React.useEffect(() => {
    const retryTimeout = retryTimeoutRef.current;
    const timeout = timeoutRef.current;

    return () => {
      clearTimeout(retryTimeout);
      clearTimeout(timeout);
    };
  }, []);

  const handleOCRStatus = React.useCallback(
    (image: string, data: OCRDetectedData) => {
      const mrzDetected = data?.mrz_detected;
      const barcodeDetected = data?.barcode_detected;

      if (!mrzDetected && !barcodeDetected) {
        onDetectionFailed?.(t('the_code_of_the_document_is_not_detected'), {
          ...data,
          image,
        });
      } else {
        onFinish?.(image, data);
      }
    },
    [t, onFinish, onDetectionFailed],
  );

  const checkOCRProcessStatus = React.useCallback(
    async ({
      image,
      processId,
      documentType,
      retries,
    }: CheckOCRStatusProps): Promise<checkOCRResponse> => {
      try {
        const data = await fetcher(checkImageOCRStatus, processId);
        const isInProgress =
          data?.status === OCR_CHECKING_STATUSES.processing ||
          data?.status === OCR_CHECKING_STATUSES.new;

        if (isInProgress) {
          const newRetries = retries || 0;
          if (newRetries >= MaxRetriesNumber) {
            onRetriesFinish?.(data, newRetries);
            return;
          }

          retryTimeoutRef.current = setTimeout(async () => {
            await checkOCRProcessStatus({
              image,
              processId,
              retries: newRetries + 1,
              documentType,
            });
          }, RetryTimeoutMs);

          return;
        }

        if (!data) {
          return;
        }
        Analytics.track(ANALYTIC_EVENTS.ivGettingResultsFromOCR, {
          check_id: data.id,
          status: data.status,
          detected_document_type: data.type_doc ?? '',
          picture_file: data.picture_file,
          mrz_detected: Boolean(data.mrz_detected),
          barcode_detected: Boolean(data.barcode_detected),
          is_barcode_detection: isBarcodeDetection(documentType),
          type: IMAGE_SERVICE_ORIGIN.ocr,
          retries: retries ?? 0,
          selected_document_type: documentType,
        });
        handleOCRStatus(image, data);
      } catch (error) {
        console.error('OCR process status check failed:', error);
        Sentry.captureException(error);

        if (error && typeof error === 'object' && 'message' in error) {
          onError?.(error as Error);
        } else if (typeof error === 'string') {
          onError?.(new Error(error));
        } else {
          onError?.(new Error('OCR process failed due to an unknown error'));
        }
      }
    },
    [checkImageOCRStatus, handleOCRStatus, onError, onRetriesFinish],
  );

  const sendAndVerifyImageForOCR = React.useCallback(
    async ({
      image,
      source,
      reservationId,
      nationality,
      documentType,
      countryCode,
    }: IVCheckProps) => {
      onBefore?.();

      try {
        const intentId = getIVIntentRemoteSession() || undefined;
        const data = await fetcher(sendImageForOCR, {
          picture_file: image,
          iv_intent_id: intentId,
          for_country: countryCode,
          reservation_id: reservationId,
          type_doc: getRemappedOfficialDocType({docType: documentType}),
          nationality,
          source,
        });
        if (!data?.id) return;

        Analytics.track(ANALYTIC_EVENTS.ivPhotoSent, {
          check_id: data.id,
          picture_file: data.picture_file,
          status: data.status,
          mrz_detected: data.mrz_detected,
          barcode_detected: Boolean(data.barcode_detected),
          is_barcode_detection: isBarcodeDetection(documentType),
          type: IMAGE_SERVICE_ORIGIN.ocr,
          selected_document_type: documentType,
        });

        await checkOCRProcessStatus({
          processId: data?.id,
          image,
        });
      } catch (error) {
        console.error('OCR image upload failed:', error);
        Sentry.captureException(error);

        if (error && typeof error === 'object' && 'message' in error) {
          onError?.(error as Error);
        } else if (typeof error === 'string') {
          onError?.(new Error(error));
        } else {
          onError?.(new Error('Image upload failed due to an unknown error'));
        }
      }
    },
    [onBefore, sendImageForOCR, checkOCRProcessStatus, onError],
  );

  const sendToOCR = React.useCallback(
    ({
      image,
      source,
      reservationId,
      nationality,
      documentType,
      countryCode,
    }: IVCheckProps) => {
      return sendImageForOCR({
        picture_file: image,
        for_country: countryCode,
        reservation_id: reservationId,
        type_doc: getRemappedOfficialDocType({docType: documentType}),
        nationality,
        source,
      });
    },
    [sendImageForOCR],
  );

  const checkOCRResult = React.useCallback(
    (sentImageId: string) => {
      return checkImageOCRStatus(sentImageId);
    },
    [checkImageOCRStatus],
  );

  return {
    sendAndVerifyImageForOCR,
    checkOCRResult,
    sendToOCR,
  };
}

export {useOCR};
